import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Copy, Check, ExternalLink } from 'lucide-react';

/**
 * Key/value property list for the Events and Sessions detail drawers.
 *
 * Both drawers grew their own copy of this list and they had drifted — different
 * label sizes, different copy-button behaviour (Sessions copied the *formatted*
 * value, Events the raw one), and Sessions could never render JSON as a block.
 * This is the single implementation, styled to match the caption/value pairing
 * the agent-run drawer's MetaCell uses.
 *
 * Row shape:
 *   { label, value, rawValue?, copyable?, mono?, to?, href?, json? }
 *   - `to`   → internal react-router link (user detail, session deep link)
 *   - `href` → external link, opens in a new tab with an icon
 *   - `mono` → render the value in the mono face (IDs, hashes, addresses)
 *   - `json` → render in a <pre> block; inferred when the value looks like JSON
 */
export default function DetailPropertyList({ rows = [], emptyMessage = 'No properties found.', idPrefix = 'prop' }) {
  const [copiedLabel, setCopiedLabel] = useState(null);

  // A single label rather than a Set: only one row's button is ever clicked at
  // a time, and the old Set version leaked entries when labels repeated.
  const copy = async (text, label) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopiedLabel(label);
      setTimeout(() => setCopiedLabel((cur) => (cur === label ? null : cur)), 2000);
    } catch {
      /* clipboard unavailable (insecure origin / denied) — nothing to recover */
    }
  };

  if (!rows.length) {
    return (
      <div className="px-4 py-8 text-center text-[12px] text-[color:var(--text-secondary)]">
        {emptyMessage}
      </div>
    );
  }

  return (
    <div className="divide-y divide-[color:var(--border-subtle)]">
      {rows.map((row, idx) => {
        // Copy the raw value when we have one — the displayed value may be
        // truncated, re-formatted, or a React element.
        const copyText =
          row.rawValue !== undefined && row.rawValue !== null
            ? typeof row.rawValue === 'object'
              ? JSON.stringify(row.rawValue, null, 2)
              : String(row.rawValue)
            : row.value != null
              ? String(row.value)
              : '';
        const canCopy = row.copyable !== false && copyText.length > 0;
        const isJson = row.json ?? (typeof row.value === 'string' && /^[[{]/.test(row.value.trim()));
        const titleText = copyText.length > 0 ? copyText.slice(0, 2000) : undefined;

        let valueNode;
        if (isJson) {
          valueNode = (
            <pre
              className="m-0 min-w-0 overflow-x-auto whitespace-pre-wrap break-all rounded bg-[color:var(--surface-sunken)] px-2 py-1 font-mono text-[11px] leading-snug text-[color:var(--text-primary)]"
              title={titleText}
            >
              {row.value}
            </pre>
          );
        } else if (row.to) {
          valueNode = (
            <Link
              to={row.to}
              title={titleText}
              className={`break-words text-[12px] text-[color:var(--text-primary)] underline-offset-2 hover:underline ${row.mono ? 'font-mono' : ''}`}
            >
              {row.value}
            </Link>
          );
        } else if (row.href) {
          valueNode = (
            <a
              href={row.href}
              target="_blank"
              rel="noopener noreferrer"
              title={titleText}
              className={`inline-flex min-w-0 items-baseline gap-1 break-all text-[12px] text-[color:var(--text-primary)] underline-offset-2 hover:underline ${row.mono ? 'font-mono' : ''}`}
            >
              <span className="min-w-0 break-all">{row.value}</span>
              <ExternalLink size={11} className="flex-none translate-y-px text-[color:var(--text-muted)]" aria-hidden />
            </a>
          );
        } else {
          valueNode = (
            <span
              className={`min-w-0 break-words text-[12px] text-[color:var(--text-primary)] ${row.mono ? 'font-mono' : ''}`}
              title={titleText}
            >
              {row.value}
            </span>
          );
        }

        return (
          <div
            key={`${idPrefix}-${row.label}-${idx}`}
            className="group/prop grid grid-cols-[160px_minmax(0,1fr)_24px] items-start gap-3 px-4 py-2 transition-colors hover:bg-surface-hover/40"
          >
            <span
              className="truncate pt-px text-[10px] font-semibold uppercase tracking-wide text-[color:var(--text-muted)]"
              title={row.label}
            >
              {row.label}
            </span>
            {valueNode}
            {canCopy ? (
              <button
                type="button"
                onClick={() => copy(copyText, row.label)}
                className="mt-px shrink-0 rounded p-1 text-[color:var(--text-muted)] opacity-0 transition-opacity duration-150 hover:bg-surface-hover hover:text-[color:var(--text-primary)] focus-visible:opacity-100 group-hover/prop:opacity-100"
                title="Copy full value"
                aria-label={`Copy ${row.label}`}
              >
                {copiedLabel === row.label ? (
                  <Check className="h-3.5 w-3.5 text-[color:var(--feedback-success)]" strokeWidth={2} />
                ) : (
                  <Copy className="h-3.5 w-3.5" strokeWidth={2} />
                )}
              </button>
            ) : (
              <span />
            )}
          </div>
        );
      })}
    </div>
  );
}
